import React from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { classes } from "./tokens";
import { buildCarServiceWhatsAppHref, PHONE_NUMBER, PHONE_NUMBER_DISPLAY } from "./whatsappConfig";
import { useCarServiceCity } from "./CarServiceCityContext";

/**
 * Dense gomechanic-style 5-column footer — adapted to Wisedrive content.
 * About · Services · By Brand · By City · Legal
 */
const ColTitle = ({ children }) => (
  <div className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-slate-400 mb-4">
    {children}
  </div>
);

const Link = ({ href, external, children }) => (
  <a
    href={href}
    target={external ? "_blank" : undefined}
    rel={external ? "noopener noreferrer" : undefined}
    className="block text-sm text-slate-300 hover:text-white transition-colors py-1"
  >
    {children}
  </a>
);

const popularBrands = [
  "Maruti Suzuki", "Hyundai", "Tata", "Mahindra", "Kia",
  "Toyota", "Honda", "Skoda", "Volkswagen", "MG",
];

const popularCities = [
  "Bengaluru", "Delhi NCR", "Mumbai", "Hyderabad", "Chennai",
  "Pune", "Kolkata", "Ahmedabad", "Jaipur", "Lucknow",
];

const CarServiceFooter = () => {
  const { selectedCity } = useCarServiceCity();
  return (
  <footer className="bg-slate-950 text-slate-300 pt-16 pb-8" data-testid="carservice-footer">
    <div className={classes.container}>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-12 gap-8 lg:gap-10 pb-12 border-b border-slate-800/80">
        {/* Brand col — full width on mobile */}
        <div className="col-span-2 md:col-span-3 lg:col-span-4">
          <img
            src="/wisedrive-logo-lp-footer.png"
            alt="Wisedrive"
            className="h-9 w-auto mb-4"
          />
          <p className="text-sm text-slate-400 leading-relaxed max-w-sm mb-5">
            Doorstep car service across 500+ Indian cities. OEM parts. Certified mechanics.
            1,000 km warranty. Service in 1 hr at your doorstep.
            <span className="block mt-2 text-xs text-slate-500">
              🇮🇳 Designed in Bengaluru. Built for Indian roads.
            </span>
          </p>
          <div className="space-y-2 text-sm">
            <a href={`tel:${PHONE_NUMBER}`} className="flex items-center gap-2 hover:text-white transition-colors">
              <Phone className="h-4 w-4 text-blue-400" />
              <span className="font-mono">{PHONE_NUMBER_DISPLAY}</span>
            </a>
            <a href="mailto:care@wisedrive.com" className="flex items-center gap-2 hover:text-white transition-colors">
              <Mail className="h-4 w-4 text-blue-400" />
              care@wisedrive.com
            </a>
            <div className="flex items-center gap-2 text-slate-500">
              <Clock className="h-4 w-4 text-blue-400" />
              Mon–Sun · 8 AM – 9 PM IST
            </div>
            <div className="flex items-start gap-2 text-slate-500">
              <MapPin className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" />
              <span>Bangalore HQ · 500+ service partners across India</span>
            </div>
          </div>
        </div>

        <div className="col-span-1 md:col-span-1 lg:col-span-2">
          <ColTitle>About</ColTitle>
          <Link href="/IN">Home</Link>
          <Link href="/IN/business">For Business</Link>
          <Link href="/IN/business#partners">Partners</Link>
          <Link href="/sample-report">Sample Report</Link>
          <Link href={buildCarServiceWhatsAppHref({ city: selectedCity, clickType: "footer_nav" })} external>WhatsApp Support</Link>
        </div>

        <div className="col-span-1 md:col-span-1 lg:col-span-2">
          <ColTitle>Services</ColTitle>
          <Link href="/carservice">General Service</Link>
          <span className="block text-sm text-slate-500 py-1">AC Service · Soon</span>
          <span className="block text-sm text-slate-500 py-1">Tyres &amp; Wheels · Soon</span>
          <span className="block text-sm text-slate-500 py-1">Battery · Soon</span>
          <span className="block text-sm text-slate-500 py-1">Denting &amp; Painting · Soon</span>
        </div>

        <div className="col-span-1 md:col-span-1 lg:col-span-2">
          <ColTitle>By Brand</ColTitle>
          {popularBrands.slice(0, 6).map((b) => (
            <span key={b} className="block text-sm text-slate-300 py-1">{b}</span>
          ))}
          <span className="block text-xs text-slate-500 mt-1">+ 6 more</span>
        </div>

        <div className="col-span-1 md:col-span-1 lg:col-span-2">
          <ColTitle>By City</ColTitle>
          {popularCities.slice(0, 6).map((c) => (
            <span key={c} className="block text-sm text-slate-300 py-1">{c}</span>
          ))}
          <span className="block text-xs text-slate-500 mt-1">+ 4 more</span>
        </div>
      </div>

      {/* Bottom row */}
      <div className="pt-6 flex flex-col-reverse lg:flex-row items-start lg:items-center justify-between gap-4 text-xs text-slate-500">
        <div className="space-y-1">
          <div>© {new Date().getFullYear()} Wisedrive Technologies Pvt. Ltd. All rights reserved.</div>
          <div className="font-mono">GST: 29AAICW0000L1Z0 · CIN: U50300KA2022PTC000000</div>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <a href="/IN/privacy" className="hover:text-white transition-colors">Privacy</a>
          <a href="/IN/terms"   className="hover:text-white transition-colors">Terms</a>
          <a href="/IN/refund"  className="hover:text-white transition-colors">Refunds</a>
          <a
            href={buildCarServiceWhatsAppHref({ city: selectedCity, clickType: "footer" })}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-600 hover:bg-green-700 text-white font-bold transition-colors"
          >
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
          </a>
        </div>
      </div>
    </div>
  </footer>
  );
};

export default CarServiceFooter;
